import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, CheckCircle2, Star, AlertTriangle, ArrowLeft, Lock } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { useStore, addAppointment, isSlotTaken, buildTimeSlots, handleImgError } from '../lib/store';
import { YearCalendar } from './YearCalendar';
import type { PaymentStatus, Appointment } from '../lib/types';
type PaymentPhase = 'idle' | 'processing' | PaymentStatus;
export function BookingFlow({
  onComplete,
  onCancel
}: {
  onComplete: () => void;
  onCancel: () => void;
}) {
  const {
    appointments,
    blocked,
    config,
    user,
    barbers,
    services
  } = useStore();
  const [step, setStep] = useState(1);
  const [selectedBarber, setSelectedBarber] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [phase, setPhase] = useState<PaymentPhase>('idle');
  const currentService = services.find(s => s.id === selectedService);
  const currentBarber = barbers.find(b => b.id === selectedBarber);
  const dateKey = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '';
  const timeSlots = buildTimeSlots(config);
  const handleNext = () => setStep(s => s + 1);
  const handleBack = () => {
    if (step === 1) onCancel();else setStep(s => s - 1);
  };
  const runPayment = (outcome: PaymentStatus) => {
    if (!currentBarber || !currentService || !selectedDate || !selectedTime) return;
    setPhase('processing');
    setTimeout(() => {
      setPhase(outcome);
      if (outcome === 'approved') {
        const appt: Appointment = {
          id: `apt_${Date.now()}`,
          barberId: currentBarber.id,
          barberName: currentBarber.name,
          barberImage: currentBarber.image,
          serviceId: currentService.id,
          serviceName: currentService.name,
          price: currentService.price,
          date: dateKey,
          time: selectedTime,
          clientName: user.name || 'Invitado',
          clientContact: user.email || user.phone || '',
          paymentStatus: 'approved',
          status: 'upcoming',
          createdAt: new Date().toISOString()
        };
        addAppointment(appt);
        setStep(5); // Go to confirmation
      } else if (outcome === 'pending') {
        toast.warning('Pago pendiente: tu lugar aún no está garantizado.');
      } else {
        toast.error('Pago rechazado. El lugar no fue reservado.');
      }
    }, 2000);
  };
  return <div className="absolute inset-0 bg-dark z-50 flex flex-col" data-id="element-42">
      <header className="flex items-center px-6 py-4 border-b border-white/5" data-id="element-43">
        <button onClick={handleBack} className="p-2 -ml-2 text-light hover:text-gold transition-colors" data-id="element-44">
          <ArrowLeft size={24} data-id="element-45" />
        </button>
        <h2 className="flex-1 text-center font-medium text-light mr-6" data-id="element-46">
          {step === 1 && 'Seleccioná barbero'}
          {step === 2 && 'Seleccioná servicio'}
          {step === 3 && 'Seleccioná fecha y hora'}
          {step === 4 && 'Pago seguro'}
          {step === 5 && '¡Turno confirmado!'}
        </h2>
      </header>

      <div className="flex-1 overflow-y-auto p-6" data-id="element-47">
        <AnimatePresence mode="wait" data-id="element-48">
          {step === 1 && <motion.div key="step1" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="space-y-4" data-id="element-49">
              {barbers.map(barber => <button key={barber.id} onClick={() => {
            setSelectedBarber(barber.id);
            handleNext();
          }} className={`w-full flex items-center gap-4 p-4 rounded-2xl border transition-all ${selectedBarber === barber.id ? 'border-gold bg-gold/5' : 'border-white/5 bg-dark-card hover:border-white/10'}`} data-id="element-50">
                  <img src={barber.image} alt={barber.name} onError={handleImgError} className="w-16 h-16 rounded-full object-cover object-[center_20%] bg-white/5" data-id="element-51" />
                  <div className="text-left flex-1" data-id="element-52">
                    <h4 className="font-medium text-light" data-id="element-53">{barber.name}</h4>
                    <p className="text-xs text-light-muted" data-id="element-54">{barber.role}</p>
                  </div>
                  <div className="flex items-center gap-1 text-gold text-xs" data-id="element-55">
                    <Star size={12} fill="currentColor" data-id="element-56" />
                    <span data-id="element-57">{barber.rating}</span>
                  </div>
                </button>)}
            </motion.div>}

          {step === 2 && <motion.div key="step2" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="space-y-4" data-id="element-58">
              {services.map(service => <button key={service.id} onClick={() => {
            setSelectedService(service.id);
            handleNext();
          }} className={`w-full text-left p-5 rounded-2xl border transition-all ${selectedService === service.id ? 'border-gold bg-gold/5' : 'border-white/5 bg-dark-card hover:border-white/10'}`} data-id="element-59">
                  <div className="flex justify-between items-start gap-3 mb-1" data-id="element-60">
                    <h4 className="font-medium text-light" data-id="element-61">{service.name}</h4>
                    <span className="font-medium text-gold whitespace-nowrap" data-id="element-62">
                      ${service.price.toLocaleString('es-UY')}
                    </span>
                  </div>
                  <p className="text-xs text-light-muted mb-2" data-id="element-63">
                    {service.desc}
                  </p>
                  <div className="flex items-center gap-1 text-[10px] text-light-muted" data-id="element-64">
                    <Clock size={12} data-id="element-65" />
                    <span data-id="element-66">{service.duration}</span>
                  </div>
                </button>)}
            </motion.div>}

          {step === 3 && <motion.div key="step3" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="space-y-6 flex flex-col h-full" data-id="element-67">
              <div data-id="element-68">
                <h3 className="text-sm font-medium text-light mb-3" data-id="element-69">
                  Elegí el día
                </h3>
                <YearCalendar selectedDate={selectedDate} onSelect={date => {
              setSelectedDate(date);
              setSelectedTime(null);
            }} disablePast data-id="element-70" />
              </div>

              <div className="flex-1" data-id="element-71">
                <h3 className="text-sm font-medium text-light mb-4" data-id="element-72">
                  Horarios disponibles
                </h3>
                {!selectedDate ? <p className="text-xs text-light-muted text-center py-8" data-id="element-73">
                    Seleccioná un día arriba.
                  </p> : <div className="grid grid-cols-4 gap-3" data-id="element-74">
                    {timeSlots.map(time => {
                const taken = isSlotTaken(appointments, blocked, selectedBarber!, dateKey, time);
                const isSelected = selectedTime === time;
                return <button key={time} disabled={taken} onClick={() => setSelectedTime(time)} className={`py-2.5 rounded-xl text-xs font-medium transition-colors ${taken ? 'bg-dark-card/50 text-light-muted/30 cursor-not-allowed' : isSelected ? 'bg-gold text-dark' : 'bg-dark-card text-light hover:bg-white/5'}`} data-id="element-75">
                          {time}
                        </button>;
              })}
                  </div>}
              </div>

              <button onClick={handleNext} disabled={!selectedDate || !selectedTime} className="w-full bg-gold text-dark py-4 rounded-xl font-semibold disabled:opacity-50 transition-opacity mt-auto" data-id="element-76">
                Continuar
              </button>
            </motion.div>}

          {step === 4 && <motion.div key="step4" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="flex flex-col h-full" data-id="element-77">
              <div className="flex-1 flex flex-col items-center justify-center text-center space-y-8" data-id="element-78">
                <div data-id="element-79">
                  <p className="text-sm text-light-muted mb-2" data-id="element-80">Total a pagar</p>
                  <p className="text-4xl font-bold text-light" data-id="element-81">
                    ${currentService?.price.toLocaleString('es-UY')},00
                  </p>
                </div>

                <div className="space-y-4" data-id="element-82">
                  <p className="text-xs text-light-muted" data-id="element-83">Vas a pagar con</p>
                  <div className="flex items-center justify-center gap-2 text-[#009EE3]" data-id="element-84">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8" data-id="element-85">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14.5v-9l6 4.5-6 4.5z" data-id="element-86" />
                    </svg>
                    <div className="text-left leading-tight" data-id="element-87">
                      <span className="font-bold text-lg block" data-id="element-88">mercado</span>
                      <span className="font-bold text-lg block -mt-1" data-id="element-89">
                        pago
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-auto space-y-4" data-id="element-90">
                <div className="flex justify-between items-center px-4 py-3 bg-dark-card rounded-xl" data-id="element-91">
                  <div data-id="element-92">
                    <p className="text-xs text-light" data-id="element-93">Tarjeta de crédito</p>
                    <p className="text-[10px] text-light-muted" data-id="element-94">
                      Visa terminada en 1234
                    </p>
                  </div>
                  <button className="text-xs text-gold" data-id="element-95">Cambiar</button>
                </div>

                <button onClick={() => runPayment('approved')} disabled={phase === 'processing'} className="w-full bg-gold text-dark py-4 rounded-xl font-semibold flex justify-center items-center gap-2 disabled:opacity-70" data-id="element-96">
                  {phase === 'processing' ? <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-5 h-5 border-2 border-dark border-t-transparent rounded-full" data-id="element-97" /> : 'Pagar ahora'}
                </button>
                <p className="text-[10px] text-center text-light-muted flex items-center justify-center gap-1" data-id="element-98">
                  <Lock size={10} data-id="element-99" /> Pago 100% seguro
                </p>
              </div>
            </motion.div>}

          {step === 5 && <motion.div key="step5" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center h-full text-center space-y-8" data-id="element-100">
              <div className="w-20 h-20 rounded-full border-2 border-gold flex items-center justify-center text-gold" data-id="element-101">
                <CheckCircle2 size={40} strokeWidth={1.5} data-id="element-102" />
              </div>

              <div data-id="element-103">
                <p className="text-sm text-light mb-1" data-id="element-104">
                  Tu turno ha sido reservado
                </p>
                <p className="text-sm text-light" data-id="element-105">con éxito.</p>
              </div>

              <div className="bg-dark-card w-full rounded-2xl p-6 space-y-4" data-id="element-106">
                <div data-id="element-107">
                  <p className="text-sm font-medium text-light capitalize" data-id="element-108">
                    {selectedDate ? format(selectedDate, "EEEE d 'de' MMMM", {
                  locale: es
                }) : ''}
                  </p>
                  <p className="text-sm font-medium text-light" data-id="element-109">
                    {selectedTime} hs
                  </p>
                </div>
                <div className="h-px bg-white/5 w-full" data-id="element-110" />
                <div className="flex items-center gap-4 text-left" data-id="element-111">
                  <img src={currentBarber?.image} alt={currentBarber?.name} onError={handleImgError} className="w-12 h-12 rounded-full object-cover object-[center_20%] bg-white/5" data-id="element-112" />
                  <div data-id="element-113">
                    <p className="text-sm font-medium text-light" data-id="element-114">
                      Con {currentBarber?.name}
                    </p>
                    <p className="text-xs text-light-muted" data-id="element-115">
                      {currentService?.name}
                    </p>
                  </div>
                </div>
              </div>

              <div className="w-full space-y-3 mt-auto" data-id="element-116">
                <button onClick={onComplete} className="w-full bg-gold text-dark py-4 rounded-xl font-semibold" data-id="element-117">
                  Ver mis turnos
                </button>
                <button onClick={onComplete} className="w-full bg-transparent text-light py-4 rounded-xl font-medium text-sm" data-id="element-118">
                  Compartir
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}